import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Phase 10.6b — Global "Needs Attention" poller.
 *
 * Polls the CRM status breakdown every 60 s while any admin surface is mounted.
 * When the count INCREASES from the last known value, emits an in-app toast
 * (never a customer WhatsApp message) and offers a shortcut to open the
 * filtered CRM Leads list.
 *
 * Read-only: never mutates leads / conversations / automation.
 *
 * Options:
 *   intervalMs         — poll frequency, default 60 000
 *   pauseWhenHidden    — pause polling while the tab is hidden (default true)
 *   showToastOnIncrease — enable/disable toast (default true)
 */
export function useNeedsAttention({
  intervalMs = 60_000,
  pauseWhenHidden = true,
  showToastOnIncrease = true,
} = {}) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const prevCountRef = useRef(null);
  const mountedRef = useRef(true);
  const nav = useNavigate();

  const fetchNow = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/crm/status-breakdown");
      const next = Number(data?.needs_attention || 0);
      const prev = prevCountRef.current;
      if (mountedRef.current) {
        setCount(next);
        setLastCheckedAt(new Date().toISOString());
      }
      // Only fire toast on subsequent polls (skip first fetch).
      if (showToastOnIncrease && prev !== null && next > prev) {
        const delta = next - prev;
        toast.warning(
          delta === 1
            ? "New WhatsApp reply needs attention"
            : `${delta} new WhatsApp replies need attention`,
          {
            duration: 8000,
            action: {
              label: "View",
              onClick: () => nav("/admin/crm?filter=needs_attention"),
            },
          },
        );
      }
      prevCountRef.current = next;
    } catch {
      // silent — background poll; keep previous count
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [showToastOnIncrease, nav]);

  useEffect(() => {
    mountedRef.current = true;
    fetchNow();  // initial fetch on mount
    let t = null;
    const start = () => {
      if (t) return;
      t = setInterval(fetchNow, intervalMs);
    };
    const stop = () => {
      if (t) { clearInterval(t); t = null; }
    };
    start();

    // Pause polling when the tab is hidden to save cycles.
    const onVis = () => {
      if (!pauseWhenHidden) return;
      if (document.visibilityState === "hidden") stop();
      else {
        // Refresh immediately on tab-focus, then resume polling.
        fetchNow();
        start();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      mountedRef.current = false;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchNow, intervalMs, pauseWhenHidden]);

  return { count, loading, lastCheckedAt, refresh: fetchNow };
}
